import React from 'react'
import { RichText } from '../../elements/RichText'
import { Text } from '../../elements/Text'
import { VideoAutoplay } from '../../elements/VideoAutoplay'
import { mq } from '../../utils'
import { Hero } from '../Hero/component'
import { CheckMarks } from '../../elements/CheckMarks'
import { Image } from '../../elements/Image'
import { ValuePropsWithModalVideo } from '../ValuePropsWithModalVideo'
import { IHeroImage, IProps, IVideo } from './index'
import { HeroWithCTA as HeroWithCTADefault } from './component'
import cn from 'classnames'
import { BrandOptions, useBrand } from '../../providers/brandProvider'

export const HeroWithCTA = (props: IProps): React.ReactElement => {
  const theme = useBrand()
  if (theme === BrandOptions.default) {
    return <HeroWithCTADefault {...props} />
  }
  return <HeroWithCTALegacy {...props} />
}

function HeroWithCTALegacy({
  eyebrow,
  headline,
  subheading,
  renderCta1,
  renderCta2,
  footnote,
  layout,
  checkmarks,
  media,
  leftAlignMedia,
  valuePropsWithModalVideo,
  areCheckmarksBelowCTA = false,
}: IProps): React.ReactElement {
  // Type Guards
  function isImage(data: IHeroImage | IVideo | undefined): data is IHeroImage {
    return !!(data as IHeroImage).imageRef
  }

  function isVideo(data: IHeroImage | IVideo | undefined): data is IVideo {
    return !!(data as IVideo).video
  }

  const isCTA2Valid = typeof renderCta2?.() !== 'undefined'

  const ctaClassnames = cn('cta-wrapper', {
    'cta-wrapper--single': !isCTA2Valid,
  })

  function renderMainContent() {
    const checkmarksContent = checkmarks && <CheckMarks {...checkmarks} />
    return (
      <>
        {valuePropsWithModalVideo && <ValuePropsWithModalVideo {...valuePropsWithModalVideo} />}
        <div className={ctaClassnames}>
          {!areCheckmarksBelowCTA ? checkmarksContent : null}
          {/* CTA 1 */}
          <div className="cta primary">{renderCta1 && renderCta1()}</div>
          {/* CTA 2 */}
          {isCTA2Valid ? <div className="cta secondary">{renderCta2?.()}</div> : null}
          {areCheckmarksBelowCTA ? (
            <div className="checkmarks-container">{checkmarksContent}</div>
          ) : null}

          {/* Footnote */}
          {footnote && (
            <div className="footnote">
              <Text size="caption">
                <RichText html={footnote} />
              </Text>
            </div>
          )}
          <style jsx>
            {`
              .cta-wrapper {
                justify-self: stretch;
              }

              :global(.cta-wrapper .list-container + .cta) {
                margin-top: 2.5rem;
              }

              .cta {
                margin: 0 0 ${isCTA2Valid ? '1rem' : '1.375rem'} 0;
              }

              .secondary {
                margin: 0 0 1.375rem;
              }

              .footnote {
                display: block;
              }

              img {
                max-width: 100%;
                margin-top: 3rem;
              }

              .checkmarks-container {
                margin-bottom: 1.375rem;
              }

              @media (${mq.tablet}) {
                .cta-wrapper {
                  justify-self: unset;
                }

                .cta {
                  display: inline-block;
                }

                .primary {
                  margin: 0 ${isCTA2Valid ? '0.9375rem' : '0'} 1.375rem 0;
                }
                .cta-wrapper--single {
                  :global(.checkmarks) {
                    justify-content: ${layout.leftAlign ? 'flex-start' : 'center'};
                  }
                }
              }

              @media (${mq.desktop}) {
                .cta-wrapper--single {
                  width: 100%;

                  .cta {
                    width: 100%;

                    :global(.email-form) {
                      max-width: 31.25rem;
                      margin: ${layout.leftAlign ? '0' : '0 auto'};
                    }
                  }
                }
              }
            `}
          </style>
        </div>
      </>
    )
  }

  function renderImage() {
    return (
      isImage(media) && (
        <>
          <Image
            classNames={'hero-cta-image'}
            altText={headline}
            borderRadius={30}
            {...media.imageRef}
          />
          <style jsx>
            {`
              img {
                max-width: 100%;
              }

              :global(.hero-cta-image) {
                border-radius: 25px;
              }
            `}
          </style>
        </>
      )
    )
  }

  function renderVideo() {
    return (
      isVideo(media) && (
        <VideoAutoplay
          video={media.video}
          videoWebm={media.videoWebm}
          fallbackImage={media.fallbackImage}
          playOnce={media.playOnce}
          alt={headline}
        />
      )
    )
  }

  const determineMediaIsImageOrVideo =
    media &&
    (isImage(media)
      ? { isBackground: media.isBackground, renderAuxContent: renderImage }
      : {
          isBackground: media.isBackground,
          renderAuxContent: renderVideo,
        })

  return (
    <Hero
      eyebrow={eyebrow}
      headline={headline}
      subheading={subheading}
      renderMainContent={renderMainContent}
      layout={layout}
      media={determineMediaIsImageOrVideo}
      leftAlignMedia={leftAlignMedia}
    />
  )
}
